/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, MouseEvent, ReactNode, useMemo, useState } from 'react';

import { Box } from '@mui/material';

// Heavily inspired by https://ibelick.com/blog/create-tilt-effect-with-react

type Props = { children: ReactNode };

const throttle = <T extends (...args: any[]) => any>(func: T, delay: number): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func(...args);
  };
};

export const TiltEffect = memo((props: Props) => {
  const [rotate, setRotate] = useState({ x: 0, y: 0 });

  const onMouseMove = useMemo(
    () =>
      throttle((e: MouseEvent) => {
        const card = e.currentTarget as HTMLElement;
        const box = card.getBoundingClientRect();
        const x = e.clientX - box.left;
        const y = e.clientY - box.top;
        const centerX = box.width / 2;
        const centerY = box.height / 2;
        const rotateX = (centerY - y) / 50;
        const rotateY = (x - centerX) / 50;

        setRotate({ x: rotateX, y: rotateY });
      }, 50),
    [setRotate],
  );

  const onMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  return (
    <Box
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg) scale3d(1, 1, 1)`,
        transition: 'all .5s cubic-bezier(0.03, 0.98, 0.52, 0.99) 0s',
      }}
    >
      {props.children}
    </Box>
  );
});
